import { type DirectiveBinding, nextTick } from 'vue';

interface ResizableBinding extends DirectiveBinding {
  value: {
    tableId: string;
    onResizeStart: () => void;
    onResizeEnd: () => void;
  };
}

interface ColumnSizes {
  [key: string]: number[];
}

const listenersMap = new WeakMap();

const initializeResizable = async (el: HTMLElement, binding: ResizableBinding) => {
  const table = el.closest('table') as HTMLTableElement | null;
  if (!table) return;
  const { tableId, onResizeStart, onResizeEnd } = binding.value || {
    tableId: 'default-table',
  };

  const savedSizes: ColumnSizes = JSON.parse(
    localStorage.getItem(`${tableId}-column-sizes`) || '[]'
  );

  await nextTick();

  const headerRow = table.querySelector('thead tr');
  const cols = headerRow ? headerRow.children : undefined;
  if (!cols) return;

  // remove existing divs
  const existingDivs = table.querySelectorAll('.resize-handle');
  existingDivs.forEach((div) => div.remove());

  for (let i = 0; i < cols.length; i += 1) {
    if (savedSizes[i]) {
      (cols[i] as HTMLElement).style.width = `${savedSizes[i]}px`;
    }
    const div = createDiv();
    (cols[i] as HTMLElement).appendChild(div);
    (cols[i] as HTMLElement).style.position = 'relative';

    setListeners(div, tableId, onResizeStart, onResizeEnd);
  }
};

const setListeners = (
  div: HTMLDivElement,
  tableId: string,
  onResizeStart: () => void,
  onResizeEnd: () => void
) => {
  let pageX: number;
  let curCol: HTMLElement | null = null;
  let nxtCol: HTMLElement | null = null;
  let curColWidth: number;
  let nxtColWidth: number;

  const saveColumnSizes = () => {
    const table = div.closest('table') as HTMLTableElement;
    const sizes = Array.from(table.querySelectorAll('th')).map((th) => th.offsetWidth);
    localStorage.setItem(`${tableId}-column-sizes`, JSON.stringify(sizes));
  };

  const mouseDownHandler = (e: MouseEvent) => {
    console.log('mouse down on size');
    onResizeStart();

    const target = e.target as HTMLElement;
    curCol = target.parentElement as HTMLElement;
    nxtCol = curCol.nextElementSibling as HTMLElement;
    pageX = e.pageX;

    const padding = paddingDiff(curCol);

    curColWidth = curCol.offsetWidth - padding;
    if (nxtCol) nxtColWidth = nxtCol.offsetWidth - padding;
  };

  const mouseOverHandler = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    target.style.borderRight = '3px solid #292929';
  };

  const mouseOutHandler = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    target.style.borderRight = '';
  };

  const mouseMoveHandler = (e: MouseEvent) => {
    if (curCol) {
      const diffX = e.pageX - pageX;

      if (nxtCol) nxtCol.style.width = `${nxtColWidth - diffX}px`;

      curCol.style.width = `${curColWidth + diffX}px`;
    }
  };

  const mouseUpHandler = () => {
    onResizeEnd();

    const isSavingLayout = JSON.parse(localStorage.getItem(`${tableId}-save-layout`) || 'false');
    if (curCol && isSavingLayout) {
      saveColumnSizes();
    }
    curCol = null;
    nxtCol = null;
    pageX = 0;
    nxtColWidth = 0;
    curColWidth = 0;
  };

  div.addEventListener('mousedown', mouseDownHandler);
  div.addEventListener('mouseover', mouseOverHandler);
  div.addEventListener('mouseout', mouseOutHandler);
  document.addEventListener('mousemove', mouseMoveHandler);
  document.addEventListener('mouseup', mouseUpHandler);

  listenersMap.set(div, {
    mouseDownHandler,
    mouseOverHandler,
    mouseOutHandler,
    mouseMoveHandler,
    mouseUpHandler,
  });
};

const createDiv = (): HTMLDivElement => {
  const div = document.createElement('div');
  div.className = 'resize-handle';
  div.style.top = '0';
  div.style.right = '0';
  div.style.width = '5px';
  div.style.position = 'absolute';
  div.style.cursor = 'col-resize';
  div.style.userSelect = 'none';
  div.style.height = '100%';
  return div;
};

const paddingDiff = (col: HTMLElement): number => {
  if (getStyleVal(col, 'box-sizing') === 'border-box') {
    return 0;
  }

  const padLeft = getStyleVal(col, 'padding-left');
  const padRight = getStyleVal(col, 'padding-right');
  return parseInt(padLeft, 10) + parseInt(padRight, 10);
};

const getStyleVal = (elm: Element, css: string): string =>
  window.getComputedStyle(elm, null).getPropertyValue(css);

export default {
  async mounted(el: HTMLElement, binding: ResizableBinding) {
    await initializeResizable(el, binding);
  },
  async updated(el: HTMLElement, binding: ResizableBinding) {
    await initializeResizable(el, binding);
  },
};
